import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import _ from 'lodash';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';
import FormHelperText from '@mui/material/FormHelperText';
import { Alert, Box, CircularProgress, IconButton, InputAdornment } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useRegisterMutation } from "@/store/services/auth";
import { toast } from "react-toastify";
import Link from "@fuse/core/Link";

/**
 * Form Validation Schema
 */
const schema = z
  .object({
    name: z.string().nonempty("You must enter your name"),
    email: z
      .string()
      .email("You must enter a valid email")
      .nonempty("You must enter an email"),
    password: z
      .string()
      .nonempty("Please enter your password.")
      .min(8, "Password is too short - should be 8 chars minimum."),
    password_confirmation: z
      .string()
      .nonempty("Password confirmation is required"),
    phone_number: z
      .string()
      .nonempty("Phone number is required")
      .regex(/^\d+$/, "Phone number must contain only numbers"),
    address: z.string().optional(),
    term_and_conditions: z
      .boolean()
      .refine((val) => val === true, "The terms and policy must be accepted."),
  })
  .refine((data) => data.password === data.password_confirmation, {
    message: "Passwords must match",
    path: ["password_confirmation"],
  });

const defaultValues = {
  name: "",
  email: "",
  password: "",
  password_confirmation: "",
  phone_number: "",
  address: "",
  term_and_conditions: false,
};

export type FormType = {
  name: string;
  email: string;
  password: string;
  password_confirmation: string;
  phone_number: string;
  address?: string;
  term_and_conditions: boolean;
};

function AuthJsCredentialsSignUpForm() {
  const navigate = useNavigate();
  const [register, { isLoading }] = useRegisterMutation();
  const { control, formState, handleSubmit, reset, watch } = useForm({
    mode: "onChange",
    defaultValues,
    resolver: zodResolver(schema),
  });

  const { isValid, dirtyFields, errors } = formState;

  const term_and_conditions = watch("term_and_conditions");

  const [showPassword, setShowPassword] = React.useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] =
    React.useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleClickShowPasswordConfirmation = () =>
    setShowPasswordConfirmation((show) => !show);
  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
  };

  async function onSubmit(formData: FormType) {
    const {
      name,
      email,
      password,
      password_confirmation,
      phone_number,
      address,
      term_and_conditions,
    } = formData;
    const trimmedAddress = (address ?? "").trim();

    const registerPayload: {
      name: string;
      email: string;
      password: string;
      password_confirmation: string;
      phone_number: string;
      term_and_conditions: boolean;
      address?: string;
    } = {
      name,
      email,
      password,
      password_confirmation,
      phone_number,
      term_and_conditions,
    };

    if (trimmedAddress) {
      registerPayload.address = trimmedAddress;
    }

    const result: any = await register(registerPayload);

    if (!result.error) {
      reset(defaultValues);
      // toast.success(`Verification Email Sent to ${email}`);
      toast.success(result?.data?.message || "Registration submitted successfully. Your account is pending admin approval.");
      navigate("/sign-in");
    } else {
      toast.error(
        result?.error?.data?.errors[0]?.detail || "Something went wrong!",
      );
    }
  }

  return (
    <form
      name="registerForm"
      noValidate
      className="mt-8 flex w-full flex-col justify-center"
      onSubmit={handleSubmit(onSubmit)}
    >
      {errors?.root?.message && (
        <Alert
          className="mb-8"
          severity="error"
          sx={(theme) => ({
            backgroundColor: theme.palette.error.light,
            color: theme.palette.error.dark,
          })}
        >
          {errors?.root?.message}
        </Alert>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="w-full mb-2 col-span-1">
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Name"
                autoFocus
                type="name"
                error={!!errors.name}
                helperText={errors?.name?.message}
                variant="outlined"
                required
                fullWidth
              />
            )}
          />
        </div>
        <div className="w-full mb-2 col-span-1">
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Email"
                type="email"
                error={!!errors.email}
                helperText={errors?.email?.message}
                variant="outlined"
                required
                fullWidth
              />
            )}
          />
        </div>
        <div className="w-full mb-2 col-span-1">
          <Controller
            name="password"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Password"
                type={showPassword ? "text" : "password"}
                error={!!errors.password}
                helperText={errors?.password?.message}
                variant="outlined"
                required
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={
                          showPassword ? "Hide password" : "Show password"
                        }
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword ? (
                          <VisibilityOff sx={{ fontSize: 19 }} />
                        ) : (
                          <Visibility sx={{ fontSize: 19 }} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </div>
        <div className="w-full mb-2 col-span-1">
          <Controller
            name="password_confirmation"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Confirm Password"
                type={showPasswordConfirmation ? "text" : "password"}
                error={!!errors.password_confirmation}
                helperText={errors?.password_confirmation?.message}
                variant="outlined"
                required
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={
                          showPasswordConfirmation
                            ? "Hide password"
                            : "Show password"
                        }
                        onClick={handleClickShowPasswordConfirmation}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPasswordConfirmation ? (
                          <VisibilityOff sx={{ fontSize: 19 }} />
                        ) : (
                          <Visibility sx={{ fontSize: 19 }} />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </div>
        <div className="w-full mb-2 col-span-2">
          <Controller
            name="phone_number"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Phone Number"
                type="tel"
                error={!!errors.phone_number}
                helperText={errors?.phone_number?.message}
                variant="outlined"
                required
                fullWidth
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                }}
                onInput={(e) => {
                  // Remove any non-numeric characters
                  const target = e.target as HTMLInputElement;
                  target.value = target.value.replace(/[^0-9]/g, "");
                  field.onChange(target.value);
                }}
              />
            )}
          />
        </div>
        <div className="w-full mb-2 col-span-2">
          <Controller
            name="address"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                multiline
                rows={6}
                placeholder="Address"
                error={!!errors.address}
                helperText={errors?.address?.message as string}
                InputLabelProps={{ shrink: true }}
                sx={{
                  "& .MuiInputBase-root": {
                    padding: "7px 0 0",
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: () => "#6d7175",
                    opacity: 1,
                  },
                }}
              />
            )}
          />
        </div>
      </div>
      <Controller
        name="term_and_conditions"
        control={control}
        render={({ field }) => (
          <FormControl error={!!errors.term_and_conditions}>
            <Box
              sx={{ display: "flex", alignItems: "center", marginLeft: -1.3 }}
            >
              <Checkbox size="small" checked={term_and_conditions} {...field} />
              <Box sx={{ marginLeft: 0 }}>
                I agree with{" "}
                <Link
                  to="/terms-of-service"
                  onClick={(e) => e.stopPropagation()}
                >
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link to="/privacy-policy" onClick={(e) => e.stopPropagation()}>
                  Privacy Policy
                </Link>
              </Box>
            </Box>
            <FormHelperText>
              {errors?.term_and_conditions?.message}
            </FormHelperText>
          </FormControl>
        )}
      />
      <Button
        variant="contained"
        color="secondary"
        className="mt-6 w-full"
        aria-label="Register"
        disabled={_.isEmpty(dirtyFields) || !isValid || isLoading}
        type="submit"
        size="large"
        startIcon={isLoading && <CircularProgress size={20} />}
      >
        Create account
      </Button>
    </form>
  );
}

export default AuthJsCredentialsSignUpForm;
